import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import SaveIcon from "@mui/icons-material/Save";
import Title from "./Title";
import { Localized } from "@fluent/react";
import { User } from "../../types/apiv1";
import { saveAs } from 'file-saver'
import { Box, Button, TableContainer } from "@mui/material";
import { useAPI } from "../../apiv1";

interface OrganizationUserListProps {
  users: User[];
}

interface SortConfig {
  key: keyof User; // Key of the user object to sort by
  direction: 'asc' | 'desc';
}

export default function OrganizationUserList(props: OrganizationUserListProps) {
  const api = useAPI();

  const [sortConfig, setSortConfig] = React.useState<SortConfig>({
    key: 'email', // Default sort by email
    direction: 'asc',
  });

  const sortedUsers = React.useMemo(() => {
    const sorted = [...props.users].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
    return sorted;
  }, [props.users, sortConfig]);

  const formattedDate = (dateString: string) => {
    const currentDate = new Date(dateString);
    const formattedDateTime = currentDate.getFullYear() + '-' +
      pad(currentDate.getMonth() + 1) + '-' +
      pad(currentDate.getDate()) + ' ' +
      pad(currentDate.getHours()) + ':' +
      pad(currentDate.getMinutes()) + ':' +
      pad(currentDate.getSeconds());

    return formattedDateTime;
  };

  function pad(number: number) {
    if (number < 10) {
      return '0' + number;
    }
    return number;
  }

  const requestSort = (key: keyof User) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const exportToCSV = () => {
    // Extract column headers
    const headers = Object.keys(sortedUsers[0]);
    // Combine headers and data
    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...sortedUsers.map((row: User) => headers.map(header => row[header as keyof User]).join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    saveAs(encodedUri, "userData.csv");
  };

  const resetPassword = async (email: string) => {
    const confirmation = window.confirm(`Are you sure you want to reset the password for ${email}?`);
    if (confirmation) {
      try {
        await api.auth.forgot(email);
      } catch (e) {
        console.log("email error is ", e);
      }
    }
  };

  return (
    <React.Fragment>
      <Box sx={{ mb: 2 }}>
        <Localized id="organization-user-list-title">
          <Title></Title>
        </Localized>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button onClick={exportToCSV} variant="outlined" startIcon={<SaveIcon />}>
          <Localized id="organization-user-export-to-csv"></Localized>
        </Button>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell onClick={() => requestSort('email')}>
                <Localized id="organization-user-email">Email</Localized>
              </TableCell>
              <TableCell onClick={() => requestSort('given_name')}>
                <Localized id="organization-user-given-name">Given Name</Localized>
              </TableCell>
              <TableCell onClick={() => requestSort('middle_names')}>
                <Localized id="organization-user-middle-names">Middle Names</Localized>
              </TableCell>
              <TableCell onClick={() => requestSort('family_name')}>
                <Localized id="organization-user-family-name">Family Name</Localized>
              </TableCell>
              <TableCell onClick={() => requestSort('created_at')}>
                <Localized id="organization-user-created-at">Created At</Localized>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedUsers.map((row) => (
              <TableRow key={row.user_id}>
                <TableCell>{row.email}</TableCell>
                <TableCell>{row.given_name}</TableCell>
                <TableCell>{row.middle_names}</TableCell>
                <TableCell>{row.family_name}</TableCell>
                <TableCell>{formattedDate(row.created_at)}</TableCell>
                <TableCell>
                  <Button onClick={() => resetPassword(row.email)} variant="outlined">
                    Reset Password
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </React.Fragment>
  );
}
